import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {CameraBrandModel, CameraModel} from '../../../../types/control';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useQuestion} from '../../../../hooks/question';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {useMapSelector} from '../../../../hooks/map-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {isValidIP, isValidPort, isValidString} from '../../../../utils/validation';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {AreaModel} from '../../../../store/control/state';
import {useMap} from '../../../../hooks/map';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings/camera';

// const CAMERA_PROTOCOL_LIST = {
//     'http': 'HTTP',
// };

// const CAMERA_PROTOCOL_SELECT_ITEM_LIST: SelectItem<string>[] =
//     Object.entries(CAMERA_PROTOCOL_LIST).map(([id, name]) => ({value: id, label: name}));

export const CameraEditScreen = memo(function CameraEditScreen() {
    const {t} = useLocalization()
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const cameraList = useSelector(state => state.control.cameraList);
    const cameraBrandOptionList: SelectItem<CameraBrandModel /*| 'manual'*/>[] = useMapSelector(
        state => state.control.cameraBrandList,
        cameraBrand => ({
            value: cameraBrand,
            label: cameraBrand.name,
        }),
        // {
        //     value: 'manual',
        //     label: 'Manual',
        // },
    );
    const areaOptionList = useMap(
        useFilterSelector(state => state.control.areaList, area => area.type === 'place', []),
        area => ({value: area, label: area.name}),
        []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => cameraList.find(device => device.id === params['id']) || null, []);
    const [name, setName] = useState(selected?.name || '');
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(
        selected == null ? areaOptionList[0] || null :
            areaOptionList.find(areaOption => areaOption.value.id === selected.area_id) || null);
    const [cameraBrandModel, setCameraBrandModel] = useState<SelectItem<CameraBrandModel /*| 'manual'*/>>(
        selected == null ? cameraBrandOptionList[0] || null :
            cameraBrandOptionList.find(cameraBrandOption => cameraBrandOption.value.id === selected.camera_brand_id)
            || null,
    );

    const [ip, setIP] = useState(selected?.ip || '');

    const [username, setUsername] = useState(selected?.username || '');
    const [password, setPassword] = useState(selected?.password || '');

    const [useRTSP, setUseRTSP] = useState(Boolean(selected?.rtsp?.port));
    const [rtspPort, setRTSPPort] = useState(selected?.rtsp?.port?.toString() || '');
    const [rtspRemotePort, setRTSPRemotePort] = useState(selected?.rtsp?.remote_port?.toString() || '');

    const [useVideoCGI, setUseVideoCGI] = useState(Boolean(selected?.video_cgi?.port));
    const [videoCGIPort, setVideoCGIPort] = useState(selected?.video_cgi?.port?.toString() || '');
    const [videoCGIRemotePort, setVideoCGIRemotePort] = useState(selected?.video_cgi?.remote_port?.toString() || '');

    const [useVideoIframe, setUseVideoIframe] = useState(Boolean(selected?.video_iframe?.port));
    const [videoIframePort, setVideoIframePort] = useState(selected?.video_iframe?.port?.toString() || '');
    const [videoIframeRemotePort, setVideoIframeRemotePort] = useState(selected?.video_iframe?.remote_port?.toString() || '');


    const [useMJPEG, setUseMJPEG] = useState(Boolean(selected?.mjpeg?.port));
    const [mjpegPort, setMJPEGPort] = useState(selected?.mjpeg?.port?.toString() || '');
    const [mjpegRemotePort, setMJPEGRemotePort] = useState(selected?.mjpeg?.remote_port?.toString() || '');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_camera_name'));

            return false;
        }

        if (areaOption?.value == null) {
            failure(t('pls_select_an_area'));

            return false;
        }

        if (cameraBrandModel == null || cameraBrandModel.value == null) {
            failure(t('pls_select_a_valid_brand_model'));

            return false;
        }

        if(!(useMJPEG || useVideoCGI || useRTSP)){
            failure(t('pls_select_at_least_one_protocol'));

            return false;
        }

        if (!isValidIP(ip)) {
            failure(t('pls_enter_a_valid_ip_address'));

            return false;
        }

        if (cameraBrandModel.value.authentication_required) {
            if (!isValidString(username)) {
                failure(t('pls_enter_a_valid_username'));

                return false;
            }

            if (!isValidString(password)) {
                failure(t('pls_enter_a_valid_password'));

                return false;
            }
        }

        if (cameraBrandModel.value.rtsp && useRTSP) {
            if (!isValidPort(rtspPort)) {
                failure('pls_enter_a_valid_rtsp_port');

                return false;
            }

            if (!isValidPort(rtspRemotePort)) {
                failure(t('pls_enter_a_valid_rtsp_remote_port'));

                return false;
            }
        }

        if (cameraBrandModel.value.mjpeg && useMJPEG) {
            if (!isValidPort(mjpegPort)) {
                failure(t('pls_enter_a_valid_mjpeg_port'));

                return false;
            }

            if (!isValidPort(mjpegRemotePort)) {
                failure(t('pls_enter_a_valid_mjpeg_remote_port'));

                return false;
            }
        }

        if (cameraBrandModel.value.video_cgi && useVideoCGI) {
            if (!isValidPort(videoCGIPort)) {
                failure(t('pls_enter_a_valid_video_cgi_port'));

                return false;
            }

            if (!isValidPort(videoCGIRemotePort)) {
                failure(t('pls_enter_a_valid_video_cgi_remote_port'));

                return false;
            }
        }
        if (cameraBrandModel.value.video_iframe && useVideoIframe) {
            if (!isValidPort(videoIframePort)) {
                failure(t('pls_enter_a_valid_video_iframe_port'));

                return false;
            }

            if (!isValidPort(videoCGIRemotePort)) {
                failure(t('pls_enter_a_valid_video_iframe_remote_port'));

                return false;
            }
        }
        return true;
    }, [
        t,
        areaOption?.value,
        cameraBrandModel,
        failure,
        ip,
        mjpegPort,
        mjpegRemotePort,
        name,
        password,
        rtspPort,
        rtspRemotePort,
        useMJPEG,
        useRTSP,
        useVideoCGI,
        useVideoIframe,
        username,
        videoCGIPort,
        videoCGIRemotePort,
        videoIframePort,
        videoIframeRemotePort
    ]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const cameraModel: CameraModel = {
            id: selected != null ? selected.id : null,
            name,
            camera_brand_id: cameraBrandModel.value.id,
            area_id: areaOption.value.id,
            username: cameraBrandModel.value.authentication_required ? username : '',
            password: cameraBrandModel.value.authentication_required ? password : '',
            ip,
        };

        if (cameraBrandModel.value.rtsp && useRTSP) {
            cameraModel.rtsp = {
                port: Number(rtspPort),
                remote_port: Number(rtspRemotePort),
            };
        }

        if (cameraBrandModel.value.mjpeg && useMJPEG) {
            cameraModel.mjpeg = {
                port: Number(mjpegPort),
                remote_port: Number(mjpegRemotePort),
            };
        }

        if (cameraBrandModel.value.video_cgi && useVideoCGI) {
            cameraModel.video_cgi = {
                port: Number(videoCGIPort),
                remote_port: Number(videoCGIRemotePort),
            };
        }

        if (cameraBrandModel.value.video_iframe && useVideoIframe) {
            cameraModel.video_iframe = {
                port: Number(videoIframePort),
                remote_port: Number(videoIframeRemotePort),
            };
        }

        return rpc.updateCamera(cameraModel)
            .then(() => {
                success(  t(cameraModel.id ? 'device_successfully_edited' : 'device_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        selected,
        name,
        cameraBrandModel?.value?.id,
        cameraBrandModel?.value?.authentication_required,
        cameraBrandModel?.value?.rtsp,
        cameraBrandModel?.value?.mjpeg,
        cameraBrandModel?.value?.video_cgi,
        cameraBrandModel?.value?.video_iframe,
        areaOption?.value?.id,
        username,
        password,
        ip,
        useRTSP,
        useMJPEG,
        useVideoCGI,
        rpc,
        failure,
        rtspPort,
        rtspRemotePort,
        mjpegPort,
        mjpegRemotePort,
        videoCGIPort,
        videoCGIRemotePort,
        success,
        onBackPressed,
    ]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_you_want_to_delete'), () => {
            return rpc.deleteCamera(selected.id)
                .then(() => {
                    success(t('camera_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadInput, setActiveNumpadExtendedInput]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: BACK_URL,
            },
        ], [t, selected, onSavePressed, onDeletePressed],
    );

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_camera_name')}
                            label={t('camera_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('location_area')}
                            options={areaOptionList}
                            onChange={setAreaOption}
                            value={areaOption}/>
                        <SettingSelectComponent
                            label={t('brand_model')}
                            options={cameraBrandOptionList}
                            onChange={setCameraBrandModel}
                            value={cameraBrandModel}/>
                        <SettingInputComponent
                            placeholder={t('write_ip')}
                            label={t('ip')}
                            value={ip}
                            setValue={setIP}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                        {
                            renderIf(cameraBrandModel?.value?.authentication_required)(() => (
                                <>
                                    <SettingInputComponent
                                        placeholder={t('write_username')}
                                        label={t('username')}
                                        value={username}
                                        setValue={setUsername}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        type='password'
                                        placeholder={t('write_password')}
                                        label={t('password')}
                                        value={password}
                                        setValue={setPassword}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                </>
                            ))
                        }
                    </div>
                    <div className='content-wrapper'>
                        {
                            renderIf(cameraBrandModel?.value?.rtsp)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='RTSP'
                                        set={() => setUseRTSP(r => !r)}
                                        value={useRTSP}/>
                                    {
                                        renderIf(useRTSP)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_port')}
                                                    label={t('port')}
                                                    value={rtspPort}
                                                    setValue={setRTSPPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_remote_port')}
                                                    label={t('remote_port')}
                                                    value={rtspRemotePort}
                                                    setValue={setRTSPRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                        {
                            renderIf(cameraBrandModel?.value?.video_cgi)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='VideoCGI'
                                        set={() => setUseVideoCGI(r => !r)}
                                        value={useVideoCGI}/>
                                    {
                                        renderIf(useVideoCGI)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_port')}
                                                    label={t('port')}
                                                    value={videoCGIPort}
                                                    setValue={setVideoCGIPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_remote_port')}
                                                    label={t('remote_port')}
                                                    value={videoCGIRemotePort}
                                                    setValue={setVideoCGIRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                        {
                            renderIf(cameraBrandModel?.value?.mjpeg)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='MJPEG'
                                        set={() => setUseMJPEG(r => !r)}
                                        value={useMJPEG}/>
                                    {
                                        renderIf(useMJPEG)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_port')}
                                                    label={t('port')}
                                                    value={mjpegPort}
                                                    setValue={setMJPEGPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_remote_port')}
                                                    label={t('remote_port')}
                                                    value={mjpegRemotePort}
                                                    setValue={setMJPEGRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                        {
                            renderIf(cameraBrandModel?.value?.video_iframe)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='Video Iframe'
                                        set={() => setUseVideoIframe(r => !r)}
                                        value={useVideoIframe}/>
                                    {
                                        renderIf(useVideoIframe)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_port')}
                                                    label={t('port')}
                                                    value={videoIframePort}
                                                    setValue={setVideoIframePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_remote_port')}
                                                    label={t('remote_port')}
                                                    value={videoIframeRemotePort}
                                                    setValue={setVideoIframeRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
